import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { withRouter } from '../../utils/withRouterCompat'
import { recordEvent } from '../../observability/events'
import clone from 'lodash/clone';
import debounce from 'lodash/debounce';
import differenceBy from 'lodash/differenceBy';
import find from 'lodash/find';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import sortBy from 'lodash/sortBy';
import moment from 'moment'
import { Q } from '@nozbe/watermelondb'
import { Box } from '@mui/material';
import AppContent from '../../components/layout/AppContent'
import Grid from '../../components/material/GridLegacyCompat';import DoneIcon from '@mui/icons-material/Done'

import { getLoggedUser } from '../../redux/auth/actions'
import { downloadProofOfManagement } from '../../syncWorker'
import { AVAILABLE_ENTITIES, Repository, utils } from '../../database'
import {
  FAZENDA,
  LOTE,
  REQUIRED_FIELD_MESSAGES,
  formatName,
} from '../../utils/formHelper'
import {
  idClOfEmpty,
  idConditionDG,
  idPregnantType,
} from '../../utils/idsHelper'
import {
  ANIMAL_DIED,
  ANIMAL_DISCARDED,
  ANIMAL_NOT_PRESENT,
  ANIMAL_PREGNANT,
  ANIMAL_UNDIAGNOSED,
  LOST_DEVICE,
} from '../../utils/constants'
import { sortList } from '../../utils/helper'
import { getBatchesByBatchIds } from '../../utils/batches'
import TopBar from '../../components/TopBar'
import TitleBar from '../../components/TitleBar'
import SearchFilterRow from '../../components/shared/SearchFilterRow'
import PageInfoStrip from '../../components/shared/PageInfoStrip'
import { makeVoiceHandler } from '../../utils/voiceSearch'
import Autocomplete, {
  autocompleteDomainValueOptionLabelSavingID,
} from '../../components/material/Autocomplete'
import ResponsiveTableWithoutInternalPagination from '../../components/ResponsiveTableWithoutInternalPagination'
import Button from '../../components/material/Button'
import { CELL_TYPES } from '../../components/PaginatedTable'
import Prompt from '../../components/Prompt'
import Loading from '../../components/Loading'
import { statisticsToDG } from './functions'
import { getPaths } from './functions/paths'

const DGInfoField = React.memo(({ row, isMerging }) => (
  <div>
    {isMerging && (
      <Grid item xs={12}>
        {row.lote}
      </Grid>
    )}
    <Grid item xs={12} style={{ fontSize: 20 }}>
      {row.codVaca}
    </Grid>
    {row.racaMatrizName && (
      <Grid item xs={12} style={{ marginTop: 4 }}>
        <b>Raça: </b>{row.racaMatrizName}
      </Grid>
    )}
    {row.ordemMatrizName && (
      <Grid item xs={12} style={{ marginTop: 2 }}>
        <b>Ordem: </b>{row.ordemMatrizName}
      </Grid>
    )}
    <Grid item xs={12} style={{ marginTop: 2 }}>
      <b>IATF: </b>{row.iatf + 1}
      {' · '}
      <b>Data: </b>{row.dataIatfFormatted}
    </Grid>
    {row.partidaName && (
      <Grid item xs={12} style={{ marginTop: 2 }}>
        <b>Sêmen: </b>{row.partidaName}
      </Grid>
    )}
    {row.previousObs && row.previousObs.map((obs, obsIdx) => (
      <Grid
        item
        key={`${row.id}_obs_${obsIdx}`}
        xs={12}
        style={{ marginTop: obsIdx === 0 ? 8 : 2 }}
      >
        <b>{obs.manejo}: </b>{obs.obs}
      </Grid>
    ))}
  </div>
))

const DGManejoField = React.memo(({
  row,
  index,
  isSaved,
  isClientUser,
  conditionsToDG,
  eccs,
  typesOfPregnancy,
  clOfEmpty,
  observations,
  updateCow,
  onSave,
  onKeyDown,
  conditionsToDGLabel,
  eccsLabel,
  typesOfPregnancyLabel,
  clOfEmptyLabel,
  normalPregnancyId,
  notEvaluatedClId,
}) => (
  <div className='grid'>
    <div
      className={`grid-item p-12 t-${!row.condicaoDG ||
        row.condicaoDG === idConditionDG.pregnantDiscard
        ? 5
        : 4
        }`}
    >
      <Autocomplete
        id={`condicaoDG_${index}`}
        options={conditionsToDG}
        onChange={(e, value) => {
          updateCow(row.id, (cow) => ({
            condicaoDG: value && value.id,
            condicaoDG_error:
              !isEmpty(value) && !isEmpty(value.id)
                ? null
                : REQUIRED_FIELD_MESSAGES.DEFAULT,
            tipoPrenhe:
              value &&
                (value.valor === 'Prenhe' ||
                  value.valor === 'Descarte Prenhe' ||
                  value.valor === 'Prenhe Touro')
                ? normalPregnancyId
                : null,
            clVazia:
              value &&
                (value.valor === 'Vazia' ||
                  value.valor === 'Descarte Vazia')
                ? notEvaluatedClId
                : null,
            dispositivoIntravaginal:
              value && cow.dispositivoIntravaginal,
            observacaoDG: observations
              .filter((obs) => obs !== LOST_DEVICE)
              .includes(cow.observacaoDG)
              ? null
              : cow.observacaoDG,
            dataDg: moment.utc(),
          }))
        }}
        onKeyDown={
          row.condicaoDG &&
          row.condicaoDG.length > 3 &&
          !isClientUser &&
          ((e) => onKeyDown(e, row.id))
        }
        value={row.condicaoDG || null}
        getOptionLabel={conditionsToDGLabel}
        useDefaultOptionSelected
        label='Diagnóstico'
        error={!isEmpty(row.condicaoDG_error)}
        helperText={row.condicaoDG_error}
      />
    </div>

    <div
      className={`grid-item p-12 t-${!row.condicaoDG ||
        row.condicaoDG === idConditionDG.pregnantDiscard
        ? 4
        : 2
        }`}
    >
      <Autocomplete
        id={`eccDG_${index}`}
        options={eccs}
        onChange={(_, value) => {
          updateCow(row.id, { eccDG: value && value.id })
        }}
        onKeyDown={
          row.eccDG &&
          row.eccDG.length > 3 &&
          !isClientUser &&
          ((e) => onKeyDown(e, row.id))
        }
        value={row.eccDG || null}
        getOptionLabel={eccsLabel}
        useDefaultOptionSelected
        label='ECC'
      />
    </div>

    {row.condicaoDG === idConditionDG.pregnant ||
      row.condicaoDG === idConditionDG.pregnantDiscard ||
      row.condicaoDG === idConditionDG.pregnantBull ? (
      <div className='grid-item p-12 t-3'>
        <Autocomplete
          id={`tipoPrenhe_${index}`}
          options={typesOfPregnancy}
          onChange={(e, value) => {
            updateCow(row.id, {
              tipoPrenhe: value && value.id,
              tipoPrenhe_error:
                isEmpty(value) ||
                (isEmpty(value.id) &&
                  REQUIRED_FIELD_MESSAGES.DEFAULT),
            })
          }}
          onKeyDown={
            row.tipoPrenhe &&
            row.tipoPrenhe.length > 3 &&
            !isClientUser &&
            ((e) => onKeyDown(e, row.id))
          }
          value={row.tipoPrenhe || null}
          getOptionLabel={typesOfPregnancyLabel}
          useDefaultOptionSelected
          label='Tipo'
          error={!isEmpty(row.tipoPrenhe_error)}
          helperText={row.tipoPrenhe_error}
        />
      </div>
    ) : row.condicaoDG === idConditionDG.empty ||
      row.condicaoDG === idConditionDG.emptyDiscard ? (
      <div className='grid-item p-12 t-3'>
        <Autocomplete
          id={`clVazia_${index}`}
          options={clOfEmpty}
          onChange={(e, value) => {
            updateCow(row.id, { clVazia: value && value.id })
          }}
          onKeyDown={
            row.clVazia &&
            row.clVazia.length > 3 &&
            !isClientUser &&
            ((e) => onKeyDown(e, row.id))
          }
          value={row.clVazia || null}
          getOptionLabel={clOfEmptyLabel}
          useDefaultOptionSelected
          label='Presença de CL'
        />
      </div>
    ) : (
      <div className='grid-item p-12 t-2' />
    )}

    <div className='grid-item p-12 t-9'>
      <Autocomplete
        id={`observacaoDG_${index}`}
        options={observations}
        onChange={(e, value) => {
          const isPresetObservation = observations
            .filter((obs) => obs !== LOST_DEVICE)
            .includes(value)

          updateCow(row.id, (cow) => ({
            observacaoDG: value,
            condicaoDG: isPresetObservation
              ? null
              : cow.condicaoDG,
            condicaoDG_error: isPresetObservation
              ? null
              : cow.condicaoDG_error,
            eccDG: isPresetObservation
              ? null
              : cow.eccDG,
            tipoPrenhe: isPresetObservation
              ? null
              : cow.tipoPrenhe,
            clVazia: isPresetObservation
              ? null
              : cow.clVazia,
            dataDg: moment.utc(),
          }))
        }}
        onKeyDown={
          row.observacaoDG &&
          row.observacaoDG.length > 3 &&
          !isClientUser &&
          ((e) => onKeyDown(e, row.id))
        }
        label='Observações'
        value={row.observacaoDG || null}
        openField
      />
    </div>

    <div
      className='grid-item p-12 t-3'
      style={{ textAlign: 'right' }}
    >
      <Button
        label='OK'
        style={{ width: '100%', marginTop: '12px' }}
        onClick={() => onSave(row.id)}
        disabled={isSaved || isClientUser}
      />
    </div>
  </div>
))

const DG = ({ ...props }) => {
  const [data, setData] = useState({
    diagnosticForAll: null,
    deviceUseForAll: null,
    searchValue: '',
    focusSearch: false,
    clearSearch: false,
    isEnterToSubmit: true,
    cowsById: Object.fromEntries(props.cowsFromCurrentIATF.map(cow => [cow.id, cow])),
    cowOrder: props.cowsFromCurrentIATF.map(cow => cow.id),
    savedCowIds: {},
  })
  const [modal, setModal] = useState({
    saveAllConfirm: false,
    finalizeConfirmation: false,
    isIncompleteWarning: false,
  })
  const [page, setPage] = useState(0)
  const [loading, setLoading] = useState(false)

  const cows = useMemo(() =>
    data.cowOrder.map(id => data.cowsById[id]).filter(Boolean),
    [data.cowOrder, data.cowsById]
  )

  const savedCows = useMemo(() =>
    Object.keys(data.savedCowIds).map(id => data.cowsById[id]).filter(Boolean),
    [data.savedCowIds, data.cowsById]
  )

  const displayCows = useMemo(() => {
    if (data.searchValue === '') return cows
    const search = data.searchValue.toLowerCase()
    return sortList(
      cows.filter(cow =>
        cow.codVaca?.toLowerCase().indexOf(search) >= 0 ||
        cow.codLeitorBastao?.toLowerCase().indexOf(search) >= 0
      ),
      'codVaca'
    )
  }, [cows, data.searchValue])

  const {
    prenhesTotal,
    vaziasTotal,
    descartesPrenhas,
    descartesVazias,
    prenhesTotalPercentage,
    vaziasTotalPercentage,
    clNao,
    clNoPercentage,
    clSim,
    clYesPercentage,
    clNaoAvaliado,
    clNotEvaluatedPercentage,
  } = useMemo(() => statisticsToDG(savedCows), [savedCows])

  // Ref for stable access to current state in memoized callbacks
  const dataRef = useRef(data)
  dataRef.current = data

  // Pre-compute lookup maps for domain values used in onChange handlers
  const normalPregnancyId = useMemo(() =>
    props.typesOfPregnancy.find(t => t.valor === 'Normal')?.id,
    [props.typesOfPregnancy]
  )
  const notEvaluatedClId = useMemo(() =>
    props.clOfEmpty.find(t => t.valor === 'Não Avaliado')?.id,
    [props.clOfEmpty]
  )

  // Memoized getOptionLabel functions — stable references for React.memo
  const conditionsToDGLabel = useMemo(() =>
    autocompleteDomainValueOptionLabelSavingID(props.conditionsToDG),
    [props.conditionsToDG]
  )
  const eccsLabel = useMemo(() =>
    autocompleteDomainValueOptionLabelSavingID(props.eccs),
    [props.eccs]
  )
  const typesOfPregnancyLabel = useMemo(() =>
    autocompleteDomainValueOptionLabelSavingID(props.typesOfPregnancy),
    [props.typesOfPregnancy]
  )
  const clOfEmptyLabel = useMemo(() =>
    autocompleteDomainValueOptionLabelSavingID(props.clOfEmpty),
    [props.clOfEmpty]
  )

  const updateCow = useCallback((cowId, changes) => {
    setData(prev => {
      const currentCow = prev.cowsById[cowId]
      const resolvedChanges = typeof changes === 'function' ? changes(currentCow) : changes
      const newSavedCowIds = { ...prev.savedCowIds }
      delete newSavedCowIds[cowId]
      return {
        ...prev,
        cowsById: {
          ...prev.cowsById,
          [cowId]: { ...currentCow, ...resolvedChanges }
        },
        savedCowIds: newSavedCowIds,
      }
    })
  }, [])

  const handleFillAll = () => {
    const cowsToFill = differenceBy(
      displayCows,
      savedCows,
      (item) => item.id
    )

    const updates = {}
    cowsToFill.forEach(cow => {
      updates[cow.id] = {
        ...cow,
        condicaoDG: !!data.diagnosticForAll && data.diagnosticForAll.id,
        condicaoDG_error: null,
        tipoPrenhe:
          !!data.diagnosticForAll &&
            (data.diagnosticForAll.id === idConditionDG.pregnant ||
              data.diagnosticForAll.id === idConditionDG.pregnantDiscard ||
              data.diagnosticForAll.id === idConditionDG.pregnantBull
            )
            ? idPregnantType.normal
            : null,
        tipoPrenhe_error: null,
        clVazia:
          !!data.diagnosticForAll &&
          (data.diagnosticForAll.id === idConditionDG.empty ||
            data.diagnosticForAll.id === idConditionDG.emptyDiscard) &&
          idClOfEmpty.notEvaluated,
        dataDg: moment.utc(),
      }
    })

    setData((prev) => ({
      ...prev,
      cowsById: { ...prev.cowsById, ...updates },
    }))
  }

  const debouncedSetSearch = useMemo(() => debounce((searchValue) => {
    setData(prev => ({ ...prev, searchValue }))
  }, 300), [])

  const handleSearch = (event, resetFilted = false) => {
    if (data.searchValue !== '') setPage(0)

    if (resetFilted) {
      debouncedSetSearch.cancel()
      setData(prev => ({ ...prev, searchValue: '' }))
    } else {
      debouncedSetSearch(event?.target?.value ?? '')
    }
  }

  const handleSaveCow = useCallback(async (cowId) => {
    recordEvent({ level: 'info', category: 'iatf', action: 'dg_cow_saved', payload: { cowId } })

    const cow = dataRef.current.cowsById[cowId]
    const { condicaoDG, observacaoDG, partidaName, lote } = cow

    const isPregnant =
      condicaoDG === idConditionDG.pregnant ||
      condicaoDG === idConditionDG.pregnantBull ||
      condicaoDG === idConditionDG.pregnantDiscard

    if (
      isEmpty(condicaoDG) &&
      (isEmpty(observacaoDG) ||
        !includes(
          [ANIMAL_NOT_PRESENT, ANIMAL_DIED, ANIMAL_UNDIAGNOSED],
          observacaoDG
        ))
    ) {
      setData((prev) => ({
        ...prev,
        cowsById: {
          ...prev.cowsById,
          [cowId]: { ...prev.cowsById[cowId], condicaoDG_error: REQUIRED_FIELD_MESSAGES.DEFAULT }
        },
      }))
      return
    }

    if (isPregnant && !cow.tipoPrenhe) {
      setData((prev) => ({
        ...prev,
        cowsById: {
          ...prev.cowsById,
          [cowId]: { ...prev.cowsById[cowId], tipoPrenhe_error: REQUIRED_FIELD_MESSAGES.DEFAULT }
        },
      }))
      return
    }

    const d0Repository = new Repository(AVAILABLE_ENTITIES.D0S)

    setData((prev) => ({
      ...prev,
      focusSearch: prev.searchValue !== '' && true,
      clearSearch: prev.searchValue !== '' && true,
      cowOrder: prev.cowOrder.filter(id => id !== cowId),
    }))

    const cowToSave = { ...cow }
    if (!cowToSave.observacaoDG || cowToSave.observacaoDG.length === 0) {
      cowToSave.observacaoDG = '[N/A]'
    }

    try {
      const { response } = await d0Repository.update({ ...clone(cowToSave) })

      response.partidaName = partidaName
      response.lote = lote

      setData((prev) => {
        const newCowsById = { ...prev.cowsById, [response.id]: response }
        const newOrder = [...prev.cowOrder, response.id]
        newOrder.sort((a, b) =>
          (newCowsById[a]?.codVaca || '').localeCompare(newCowsById[b]?.codVaca || '')
        )

        return {
          ...prev,
          cowsById: newCowsById,
          cowOrder: newOrder,
          savedCowIds: { ...prev.savedCowIds, [response.id]: true },
          searchValue: '',
          focusSearch: prev.searchValue !== '' && false,
          clearSearch: prev.searchValue !== '' && false,
        }
      })
    } catch (error) {
      console.error(
        `There was a problem updating cow ${cow.id}:${cow.codVaca}: ${error}`
      )

      setData((prev) => {
        const newOrder = [...prev.cowOrder, cowId]
        newOrder.sort((a, b) =>
          (prev.cowsById[a]?.codVaca || '').localeCompare(prev.cowsById[b]?.codVaca || '')
        )
        return { ...prev, cowOrder: newOrder }
      })
    } finally {
      debouncedSetSearch.cancel()
      if (dataRef.current.searchValue !== '') setPage(0)
      setData(prev => ({ ...prev, searchValue: '' }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleOnEnterPress = useCallback((e, cowId) => {
    const currentData = dataRef.current
    const isASavedCow = currentData.savedCowIds[cowId]

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      setData((prev) => ({
        ...prev,
        isEnterToSubmit: false,
      }))
    }

    if (e.key === 'Enter') {
      setData((prev) => ({
        ...prev,
        isEnterToSubmit: true,
      }))
    }

    if (!isASavedCow) {
      if (e.key === 'Enter' && e.shiftKey === false && currentData.isEnterToSubmit) {
        e.preventDefault()
        handleSaveCow(cowId)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleSaveCow])

  const handleSaveAllCows = async () => {
    setLoading(true)
    setPage(0)

    const cowsToSave = differenceBy(
      displayCows,
      savedCows,
      (item) => item.id
    )

    for (const cow of cowsToSave) {
      handleSaveCow(cow.id)
    }

    setTimeout(() => {
      setLoading(false)
      setModal({
        ...modal,
        saveAllConfirm: false,
      })
    }, 1200)
  }

  const validateDGs = () => {
    return !cows.some(cow => !data.savedCowIds[cow.id])
  }

  const handleFinalize = async () => {
    recordEvent({ level: 'info', category: 'iatf', action: 'dg_finalized' })

    const batchRepository = new Repository(AVAILABLE_ENTITIES.BATCHES)

    for (const batch of props.batches.data) {
      await batchRepository.update({
        id: batch.id,
        isFinalize: true,
        dias: props.protocol.currentDay,
        processoAtual: 'DG',
      })

      if (props.path.hasCorralDefault) {
        props.history.push(`/farms/${props.path.farmId}`)
      } else {
        props.history.push(
          `/farms/${props.path.farmId}/corrals/${props.path.corralId}`
        )
      }
    }

    downloadProofOfManagement({
      values: ['lotes', 'd0s'],
      text:
        'Comprovante de manejo DG - ' +
        formatName(
          props.batches.data.map((item) => item.nomeLote),
          LOTE
        ) +
        ' - ' +
        formatName(props.path.farm, FAZENDA) +
        ' - ' +
        moment.utc().valueOf() +
        '.progerar',
      batchIds: props.match.params.id.split(';'),
    })
  }

  useEffect(() => {
    const initialSavedCowIds = {}
    props.cowsFromCurrentIATF.forEach(cow => {
      if (!!cow.dataDg) {
        initialSavedCowIds[cow.id] = true
      }
    })

    setData(prev => ({ ...prev, savedCowIds: initialSavedCowIds }))

    return () => debouncedSetSearch.cancel()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <AppContent>
      <TopBar title={formatName('NOME TESTE', FAZENDA)} />
      <TitleBar
        title={
          <>
            Protocolo: {props.protocol.name}
            <br />
            Manejo: Diagnóstico de Gestação
          </>
        }
        buttons={[
          {
            onClick: () =>
              validateDGs()
                ? setModal({ finalizeConfirmation: true })
                : setModal({ isIncompleteWarning: true }),
            icon: <DoneIcon />,
            disabled:
              prenhesTotal === cows.length && props.isClientUser && true,
            label: 'Finalizar',
          },
        ]}
        paths={getPaths({
          path: props.path,
          isMerging: props.isMerging,
          label: 'Diagnóstico de Gestação',
        })}
      />
      {loading && <Loading />}
      <Box className='app-main-container'>
        <SearchFilterRow
          preChildren={(() => {
            const totalSaved = savedCows.length
            const totalCows = cows.length
            const pct = totalCows > 0 ? Math.round((totalSaved / totalCows) * 100) : 0
            const stats = [
              {
                label: 'Matrizes',
                value: totalSaved,
                total: totalCows,
                sub: `${pct}% diagnósticadas`,
                tone: pct >= 100 ? 'success' : pct > 0 ? 'primary' : 'default',
              },
              {
                label: 'Prenhes',
                value: prenhesTotal,
                total: totalCows,
                sub: `${prenhesTotalPercentage}%`,
                tone: prenhesTotal > 0 ? 'success' : 'default',
              },
              {
                label: 'Vazias',
                value: vaziasTotal,
                total: totalCows,
                sub: `${vaziasTotalPercentage}%`,
                tone: 'default',
              },
            ]
            const datasChips = []
            if (props.protocol.iatfDate)
              datasChips.push({ label: 'Data da IATF', value: props.protocol.iatfDate })

            const resultadosChips = [
              { label: 'Descartes', value: `${descartesPrenhas} Prenhes · ${descartesVazias} Vazias` },
              { label: 'Presença de CL', value: `${clNaoAvaliado} (${clNotEvaluatedPercentage}%) N/A · ${clNao} (${clNoPercentage}%) Não · ${clSim} (${clYesPercentage}%) Sim` },
            ]

            const chipGroups = [
              ...(datasChips.length > 0 ? [{ category: 'Datas', chips: datasChips }] : []),
              { category: 'Resultados', chips: resultadosChips },
            ]
            return <PageInfoStrip stats={stats} chipGroups={chipGroups} />
          })()}
          onSearchChange={(e) => handleSearch(e)}
          onVoiceClick={makeVoiceHandler(handleSearch)}
          searchAutoFocus={data.focusSearch}
        />
        <div className='grid grid-dg'>
          <div className='grid-item p-12 t-4 p-margin-bottom-3'>
            <Autocomplete
              id='diagnosticForAll'
              options={props.conditionsToDG}
              value={data.diagnosticForAll || null}
              label='Diagnóstico'
              getOptionLabel={conditionsToDGLabel}
              onChange={(_, value) => {
                setData({
                  ...data,
                  diagnosticForAll: value,
                })
              }}
              tabIndex={data.searchValue !== '' ? -1 : null}
            />
          </div>
          <div className='grid-item p-12 t-7 d-4'>
            <Button
              label='Preencher Todas'
              style={{
                width: '48%',
                height: '46px',
                marginTop: '10px',
                padding: 0,
              }}
              onClick={
                (!!data.diagnosticForAll || !!data.deviceUseForAll) &&
                handleFillAll
              }
              tabIndex={data.searchValue !== '' ? -1 : null}
            />
            <Button
              label='Salvar todas'
              tabIndex={data.searchValue !== '' ? -1 : null}
              style={{
                width: '48%',
                marginLeft: '4%',
                height: '46px',
                marginTop: '10px',
              }}
              disabled={
                savedCows.length === cows.length || props.isClientUser
              }
              onClick={() => setModal({ saveAllConfirm: true })}
            />
          </div>
          <div className='p-12'>
            <ResponsiveTableWithoutInternalPagination
              columns={[
                {
                  name: 'Matriz (ID)',
                  type: CELL_TYPES.FIELD,
                  field: (row) => (
                    <DGInfoField
                      row={row}
                      isMerging={props.isMerging}
                    />
                  ),
                  grid: 3,
                },
                {
                  name: 'Manejo',
                  type: CELL_TYPES.FIELD,
                  field: (row, index) => (
                    <DGManejoField
                      row={row}
                      index={index}
                      isSaved={!!data.savedCowIds[row.id]}
                      isClientUser={props.isClientUser}
                      conditionsToDG={props.conditionsToDG}
                      eccs={props.eccs}
                      typesOfPregnancy={props.typesOfPregnancy}
                      clOfEmpty={props.clOfEmpty}
                      observations={props.observations}
                      updateCow={updateCow}
                      onSave={handleSaveCow}
                      onKeyDown={handleOnEnterPress}
                      conditionsToDGLabel={conditionsToDGLabel}
                      eccsLabel={eccsLabel}
                      typesOfPregnancyLabel={typesOfPregnancyLabel}
                      clOfEmptyLabel={clOfEmptyLabel}
                      normalPregnancyId={normalPregnancyId}
                      notEvaluatedClId={notEvaluatedClId}
                    />
                  ),
                  grid: 9,
                },
              ]}
              data={displayCows}
              page={page}
              onPageChange={(_, page) => setPage(page)}
              className='table-dg'
            />
          </div>
        </div>

        <Prompt
          visible={modal.finalizeConfirmation}
          title='Deseja Finalizar?'
          message='Uma vez finalizado os dados não salvos serão perdidos.'
          buttons={[
            {
              label: 'Não',
              onClick: () => setModal({ finalizeConfirmation: false }),
            },
            {
              label: 'Sim',
              onClick: handleFinalize,
            },
          ]}
        />

        <Prompt
          visible={modal.saveAllConfirm}
          title='Confirmação:'
          message={`Deseja realmente salvar todas?`}
          buttons={[
            {
              label: 'Não',
              onClick: () =>
                setModal({
                  saveAllConfirm: false,
                }),
            },
            {
              label: 'Sim',
              onClick: handleSaveAllCows,
            },
          ]}
        />

        <Prompt
          visible={modal.isIncompleteWarning}
          title='Erro'
          message='Existem matrizes que não foram salvas.'
          buttons={[
            {
              label: 'OK',
              onClick: () => setModal({ isIncompleteWarning: false }),
            },
          ]}
        />
      </Box>
    </AppContent>
  )
}

const propsFromDatabase = async (props) => {
  const farm = props.location.state.farm
  const corral = props.location.state?.retiro
  const batchIds = props.match.params.id.split(';')
  const isMerging = batchIds.length > 1
  // Group 1: independent queries
  const [batches, loggedUser, partidas] = await Promise.all([
    getBatchesByBatchIds(batchIds),
    getLoggedUser(),
    utils.getWithParam(AVAILABLE_ENTITIES.PARTIDA, 'farm_id', farm.id),
  ])
  const isClientUser = loggedUser.roles[0] === 'Cliente (somente visualização)'

  let deadCows = []

  // Group 2: depend on group 1 results but not on each other
  const [cowsFromCurrentIATFRaw, protocol, bulls, ordens, racas] = await Promise.all([
    utils.getWithParam(AVAILABLE_ENTITIES.D0S, 'batch_id', Q.oneOf(batches.map((b) => b.id))),
    utils.getElement(AVAILABLE_ENTITIES.PROTOCOLS, batches[0].protocoloId),
    utils.getWithParam(AVAILABLE_ENTITIES.BULLS_NEW, 'id', Q.oneOf(partidas.map((f) => f.touroId))),
    utils.getDomainValuesBy('Ordem da Matriz Predominante'),
    utils.getDomainValuesBy('Raça da Matriz predominante'),
  ])

  let cowsFromCurrentIATF = cowsFromCurrentIATFRaw

  cowsFromCurrentIATF = await Promise.all(
    cowsFromCurrentIATF.map(async (cow) => {
      cow.lote = find(batches, { id: cow.loteId }).nomeLote

      const iatf = await utils.getWithParam(
        AVAILABLE_ENTITIES.IATFS,
        'vacaId',
        cow.id
      )

      const dns = await utils.getWithParam(
        AVAILABLE_ENTITIES.DNS,
        'vacaId',
        cow.id
      )

      if (
        iatf[0]?.observacao === ANIMAL_DISCARDED ||
        iatf[0]?.observacao === ANIMAL_DIED ||
        dns[0]?.observacao === ANIMAL_DIED ||
        dns[0]?.observacao === `[${ANIMAL_DIED}] ${ANIMAL_DIED}` ||
        cow.observacaoD0 === ANIMAL_DIED ||
        cow.observacaoD0 === ANIMAL_PREGNANT
      ) {
        deadCows.push(cow.id)
      }

      const partida = find(partidas, { id: iatf[0]?.partidaId })
      const bull = !!partida && find(bulls, { id: partida.touroId })

      if (!!!bull && !!!partida) {
        cow.partidaName = null
      } else {
        cow.partidaName = `${!!bull ? bull.nome : ''} - ${!!partida &&
          (partida?.codigo || moment(partida?.data).format('DD/MM/YYYY') || '')
          }`
      }

      return cow
    })
  )

  const managementProtocols = await Promise.all(
    protocol.managementProtocols.map(async (selectedProtocol) => {
      const management = await utils.getElement(
        AVAILABLE_ENTITIES.DOMAIN_VALUES,
        selectedProtocol.managementId
      )
      const day = await utils.getElement(
        AVAILABLE_ENTITIES.DOMAIN_VALUES,
        selectedProtocol.day
      )

      return {
        management: management?.valor,
        day,
      }
    })
  )
  const dayOfIatf = managementProtocols.find(
    (protocol) => protocol.management === 'IATF'
  )?.day?.valor
  const iatfDate = moment(batches[0].dataIatf)
    .add(parseInt(dayOfIatf.replace(/\D/g, '')), 'days')
    .format('DD/MM/YYYY')

  cowsFromCurrentIATF.forEach(cow => {
    const ordemDV = find(ordens, { id: cow.ordemMatriz })
    cow.ordemMatrizName = ordemDV ? ordemDV.descricao || ordemDV.valor : null
    const racaDV = find(racas, { id: cow.racaMatriz })
    cow.racaMatrizName = racaDV ? racaDV.descricao || racaDV.valor : null
    cow.dataIatfFormatted = iatfDate
  })

  // Group 3: domain values (all independent)
  const [conditionsToDGRaw, deviceUsesRaw, eccsRaw, typesOfPregnancy, clOfEmpty, devices] = await Promise.all([
    utils.getDomainValuesBy('Condição DG'),
    utils.getDomainValuesBy('Tipo de Implante Predominante'),
    utils.getDomainValuesBy('ECC'),
    utils.getDomainValuesBy('Tipo Prenhe'),
    utils.getDomainValuesBy('CL'),
    utils.getDomainValuesBy('Dispositivo'),
  ])
  const conditionsToDG = sortBy(conditionsToDGRaw, [
    (condicao) => condicao.valor !== 'Prenhe',
    (condicao) => condicao.valor !== 'Vazia',
    (condicao) => condicao.valor !== 'Descarte Prenhe',
    (condicao) => condicao.valor !== 'Descarte Vazia',
    (condicao) => condicao.valor !== 'Prenhe Touro',
  ])
  const deviceUses = sortBy(deviceUsesRaw, ['valor'])
  const eccs = sortBy(eccsRaw, [(ecc) => parseFloat(ecc.valor)])
  const observations = [ANIMAL_NOT_PRESENT, ANIMAL_DIED, ANIMAL_UNDIAGNOSED]

  return {
    conditionsToDG,
    deviceUses,
    eccs,
    typesOfPregnancy,
    clOfEmpty,
    devices,
    observations,
    isMerging,
    protocol: {
      name: protocol.name,
      iatfDate,
    },
    batches: {
      data: batches,
    },
    cowsFromCurrentIATF: sortList(
      cowsFromCurrentIATF.filter(
        (cow) => !cow.omit_from_dg_final && deadCows.indexOf(cow.id) === -1
      ),
      'codVaca'
    ),
    path: {
      farmId: farm.id,
      farm: farm.label,
      batchId: isMerging
        ? null
        : !!batches[0].originalBatchId
          ? batches[0].originalBatchId
          : batches[0].id,
      batch: isMerging ? 'Vários Lotes' : batches[0].nomeLote,
      corralId: corral.id,
      corral: corral.label,
      hasCorralDefault: corral.label === 'Retiro DEFAULT',
    },
    isClientUser,
  }
}

export default connect()(
  utils.withPropsFromDatabase(propsFromDatabase, withRouter(DG))
)
